import type { Loan } from '@/types'
import { buildCoverageRows, type CoverageRow } from '@/components/deal/dossier/economicsCoverage'
import { isLodging } from '@/components/deal/dossier/lodging'
import { customValue } from './loanFigures'

/**
 * A catalogue field's value read off a pipeline row.
 *
 * The row carries a handful of catalogue fields as top-level properties
 * (`loanAmount`, `rate`, `noi`, `dscr`, the asset class as `type`) and the rest in
 * `customFields`, camel-cased. A zero on a column that is NOT NULL is "no package
 * stated one", never a figure — the same reading `commitmentSplit` gives it.
 */
function loanCatalogueValue(loan: Loan, name: string): unknown {
  switch (name) {
    case 'loan_amount':
      return loan.loanAmount > 0 ? loan.loanAmount : undefined
    case 'rate':
      return loan.rate || undefined
    case 'asset_type':
      return loan.type
    case 'noi':
      return loan.noi != null && loan.noi > 0 ? loan.noi : customValue(loan, 'noi')
    case 'dscr':
      return loan.dscr > 0 ? loan.dscr : undefined
    default:
      return customValue(loan, name)
  }
}

/**
 * The Deal Inspector's debt-service-coverage rows: the platform's interest-only
 * coverage (Actual/360 unless the deal says otherwise), the broker-stated DSCR
 * kept apart from it, and — for a hotel — the figure after the entered FF&E
 * reserve. One builder shared with the dossier's Economics tab, so the two
 * screens cannot disagree about a deal.
 */
export function loanCoverageRows(loan: Loan): CoverageRow[] {
  return buildCoverageRows({
    get: (name) => loanCatalogueValue(loan, name),
    lodging: isLodging(loan.type),
    derivedFields: loan.derivedFields ?? [],
  })
}
